/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  IonButton,
  IonButtons,
  IonCol,
  IonGrid,
  IonInput,
  IonLabel,
  IonRow,
} from '@ionic/react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ENVIRONMENT from '@/environments/environment';
import { AnalyticsOrigination, AnalyticsStepNumber } from '@/interfaces/AnalyticsInterface/AnalyticsInterface';
import {
  Customer,
  CustomerClassificationEnum,
} from '@/interfaces/CustomerInterface/CustomerInterface';
import { DecisionStatus } from '@/interfaces/DecisionInterface/DecisionInterface';
import { EDNIModalRoutes } from '@/interfaces/EDniModalInterface/EDniModalInterface';
import { KeyModel } from '@/redux/eDniModal/appConfig/appConfig';
import { saveCreditProposal } from '@/redux/eDniModal/creditProposal/creditProposal';
import { saveCustomerClassification } from '@/redux/eDniModal/customerClassification/customerClassification';
import {
  createProposal,
  getCountryList,
  getDecision,
  postClassification,
} from '@/redux/eDniModal/eDniModalAPI';
import {
  getTimeResult, registerLoanRefused, registerPersonalDataContinue, resolveAgeSegment,
} from '@/services/analytics';
import { RootState } from '@/store';
import { validateSurname } from '@/utils/lastNameHelper';
import { VALIDATOR_PATTERN } from '@/utils/validators';

import styles from './PersonalData.module.scss';

interface PersonalDataProps {
  nextStep: (arg0: EDNIModalRoutes) => void;
  handleLoading: (arg0: boolean) => void;
  errorType?: (arg0: string) => void;
  sendIFrameUrl: (url: string) => void;
}
export default function PersonalData({
  nextStep,
  handleLoading,
  errorType,
  sendIFrameUrl,
}: PersonalDataProps) {
  const dispatch = useDispatch();
  const product = useSelector((state: RootState) => state.product?.data);
  const simulation = useSelector(
    (state: RootState) => state.loanSimulation.data,
  );
  const dniData = useSelector((state: RootState) => state.dniData?.data);
  const creditProposal = useSelector(
    (state: RootState) => state.creditProposal.data,
  );

  const [countryList, setCountryList] = useState<KeyModel[]>([]);
  const [emailValue, setEmailValue] = useState<string>('');
  const [phoneValue, setPhoneValue] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string>('');
  const [isEmailTouched, setIsEmailTouched] = useState<boolean>(false);
  const [isPhoneTouched, setIsPhoneTouched] = useState<boolean>(false);
  const [initTime] = useState<dayjs.Dayjs>(dayjs());

  // console.log('dniData', dniData);

  const changeEmailInput = (value: string, setTouched = false) => {
    if (!isEmailTouched && !setTouched) return;
    if (!isEmailTouched && setTouched) {
      setIsEmailTouched(true);
    }
    if (value) {
      if (!VALIDATOR_PATTERN.EMAIL.test(value)) {
        setEmailError('Introduce un email válido');
      } else {
        setEmailError('');
        setEmailValue(value);
      }
    } else {
      setEmailError('Introduce un email válido');
    }
  };

  const changePhoneInput = (value: string, setTouched = false) => {
    if (!isPhoneTouched && !setTouched) return;
    if (!isPhoneTouched && setTouched) {
      setIsPhoneTouched(true);
    }
    let validPhone;
    for (let i = 1; i < value.length && !validPhone; i++) {
      if (value[i] !== value[1]) {
        validPhone = true;
      } else {
        validPhone = false;
      }
    }
    if (!value) {
      setPhoneError('Indica tu móvil');
    } else if (!VALIDATOR_PATTERN.PHONE.test(value) || !validPhone) {
      setPhoneError('Móvil inválido');
    } else {
      setPhoneError('');
      setPhoneValue(value);
    }
  };

  const getCountries = async () => {
    try {
      const countries = await getCountryList();
      setCountryList(countries[0].keys);
    } catch (error) {
      nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
      handleLoading(false);
    }
  };

  const getDecisionData = async (creditProposalId: string, counter: number) => {
    counter++;
    try {
      handleLoading(true);
      const leadToken = sessionStorage.getItem('leadToken');
      const decisionResp = await getDecision(creditProposalId);
      if (decisionResp) {
        switch (decisionResp.decision) {
          case DecisionStatus.APPROVED:
            nextStep(EDNIModalRoutes.OPERATION_SHIPPING);
            handleLoading(false);
            break;
          case DecisionStatus.DENIED:
            registerLoanRefused({ origin: AnalyticsOrigination.App, uuid: creditProposal?.credit_proposal_id }, 'DEC');
            nextStep(EDNIModalRoutes.DENIAL);
            handleLoading(false);
            break;
          case DecisionStatus.MISSING_DATA:
            nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
            handleLoading(false);
            break;
          case DecisionStatus.COUNTER_OFFER:
            const continuationUrl = `${ENVIRONMENT.URL_PEPPERONE}/?lead_token=${leadToken}&credit_proposal_id=${creditProposalId}&super_express=true&is_app=true`;
            handleLoading(true);
            sendIFrameUrl(continuationUrl);
            break;
          default:
            nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
            handleLoading(false);
            break;
        }
      }
    } catch (error: any) {
      if (error.result.http_code === 409 && counter <= 11) {
        setTimeout(() => {
          getDecisionData(creditProposalId, counter);
        }, 3000);
      } else {
        nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
        handleLoading(false);
      }
    }
  };

  const getProposalData = async () => {
    // console.log('getProposalData', dniData);
    let customer: Customer | null = null;
    if (dniData) {
      customer = {
        document_id: dniData?.document_id,
        document_id_expiration_date: dniData?.expiration_date,
        name: dniData?.name,
        surname1: dniData?.last_name
          ? validateSurname(dniData?.last_name).surnameOne
          : '',
        surname2: dniData?.last_name
          ? validateSurname(dniData?.last_name).surnameTwo
          : '',
        nationality: dniData?.nationality,
        birth_country: dniData?.birth_country || dniData?.nationality,
        birth_date: dniData?.birth_date,
        gender: dniData?.gender,
        email: emailValue,
        phones: [phoneValue],
      };
    }

    const data = {
      commercial_offer_id: simulation?.commercial_offer_id,
      product: simulation?.flow,
      term: simulation?.term,
      customer,
      requested_amount: simulation
        ? simulation.total_cost - simulation.opening_fee
        : 0,
      purchase_amount: simulation
        ? simulation.total_cost - simulation.opening_fee
        : 0,
      good_type: product?.code,
      ocr_id: dniData?.ocr_id,
    };

    try {
      handleLoading(true);
      const proposalResp = await createProposal(data);
      dispatch(saveCreditProposal(proposalResp));
      //   InsightService.logTrace('CreditProposal', [
      //     { creditProposalId: response.credit_proposal_id },
      //   ]);
      const classificationBody = {
        commercial_offer_id: simulation?.commercial_offer_id,
        // product: simulation?.flow,
        term: simulation?.term,
        customer,
        requested_amount: simulation?.financed_amount,
        purchase_amount: simulation?.financed_amount,
        // good_type: product?.code
      };
      const classification = await postClassification(
        proposalResp.credit_proposal_id,
        classificationBody,
      );
      dispatch(saveCustomerClassification(classification));
      const personalDataAnalytics = {
        time_elapsed: getTimeResult(initTime),
        step_number: AnalyticsStepNumber.PersonalData,
        cust_age_segment: proposalResp.customer?.birth_date ? resolveAgeSegment(proposalResp.customer.birth_date) : '',
        cust_birth_country: proposalResp.customer?.birth_country,
        cust_nationality: proposalResp.customer?.nationality,
        cust_email_domain: proposalResp.customer?.email,
        cust_classification: classification.customer_classification,
        prop_require_documentation: classification.require_documentation,
        prop_require_labor_data: classification.require_labor_data,
        prop_require_secure_payment: classification.require_secure_payment,
        prop_require_address_data: classification.require_address_data,
        prop_require_enrichment_personal_finances: classification.require_enrichment_personal_finances,
        prop_source_application: proposalResp.source_application,
        prop_product: proposalResp.product,
        prop_amount: proposalResp.loan_amount,
      };
      registerPersonalDataContinue({ origin: AnalyticsOrigination.App, uuid: proposalResp?.credit_proposal_id || '' }, personalDataAnalytics);
      if (
        classification.customer_classification
        === CustomerClassificationEnum.denied
      ) {
        registerLoanRefused({ origin: AnalyticsOrigination.App, uuid: proposalResp?.credit_proposal_id || '' }, 'CUST');
        nextStep(EDNIModalRoutes.DENIAL);
        handleLoading(false);
      } else if (
        classification.require_labor_data
        || classification.require_address_data
      ) {
        nextStep(EDNIModalRoutes.LABOR_DATA);
        handleLoading(false);
      } else {
        await getDecisionData(proposalResp.credit_proposal_id, 0);
      }
      // handleLoading(false);
    } catch (error: any) {
      handleLoading(false);
      if (error?.result?.errors) {
        if (error.result.errors.find((er: any) => er.code === 'DRF0081')) {
          nextStep(EDNIModalRoutes.EXISTING_RFN);
        } else if (
          error.result.errors.find((er: any) => er.code === 'DRF0089')
        ) {
          if (errorType) {
            errorType('DRF0089');
          }
          nextStep(EDNIModalRoutes.PERSONAL_FINANCES_UNAVAILABLE);
        } else if (
          error.result.errors.find((er: any) => er.code === 'DRF0092')
        ) {
          if (errorType) {
            errorType('DRF0092');
          }
          nextStep(EDNIModalRoutes.PERSONAL_FINANCES_UNAVAILABLE);
        } else {
          nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
        }
      } else {
        nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
      }
    }
  };

  const handleContinue = () => {
    getProposalData();
  };

  useEffect(() => {
    getCountries();
  }, []);

  return (
    <div className={styles.modalPersonalDataContainer}>
      <div className={styles.personalDataForm}>
        <IonLabel className={styles.modalTitle}>Datos de contacto</IonLabel>
        <IonGrid className={styles.modalFormWrapper}>
          <IonRow className={styles.modalFormRow}>
            <IonCol>
              <IonInput
                type="email"
                label="Email"
                labelPlacement="floating"
                className={classNames('system-font', styles.upperInput, {
                  'ion-invalid': isEmailTouched && emailError,
                  'ion-valid': phoneError === '',
                })}
                errorText={emailError}
                onIonInput={(e: any) => {
                  changeEmailInput(e.target.value);
                }}
                onIonBlur={(e: any) => changeEmailInput(e.target.value, true)}
              />
              {emailError && <span className={styles.validationError}>{emailError}</span>}
            </IonCol>
          </IonRow>
          <IonRow className={styles.modalFormRow}>
            <IonCol>
              <IonInput
                type="tel"
                maxlength={9}
                label="Teléfono móvil"
                labelPlacement="floating"
                className={classNames(styles.formAmountInput, styles.lowerInput, {
                  'ion-invalid ': isPhoneTouched && phoneError,
                  'ion-valid': phoneError === '',
                })}
                errorText={phoneError}
                onIonInput={(e: any) => changePhoneInput(e.target.value)}
                onIonBlur={(e: any) => changePhoneInput(e.target.value, true)}
              />
              {phoneError && <span className={styles.validationError}>{phoneError}</span>}
            </IonCol>
          </IonRow>
        </IonGrid>
      </div>
      <div className={styles.personalDataForm}>
        <IonLabel className={styles.modalTitle}>Datos personales</IonLabel>
        <IonGrid className={styles.modalFormWrapper}>
          <IonRow className={styles.modalFormRow}>
            <IonCol>
              <IonInput
                label="Nombre"
                labelPlacement="stacked"
                className={classNames(styles.upperInput, 'system-font')}
                value={dniData?.name}
                disabled={!!dniData?.document_id}
              />
            </IonCol>
          </IonRow>
          <IonRow className={styles.modalFormRow}>
            <IonCol>
              <IonInput
                label="Apellidos"
                labelPlacement="stacked"
                className={classNames('system-font')}
                value={dniData?.last_name}
                disabled={!!dniData?.document_id}
              />
            </IonCol>
          </IonRow>
          <IonRow className={styles.modalFormRow}>
            <IonCol>
              <IonInput
                label="DNI o NIE"
                labelPlacement="stacked"
                value={dniData?.document_id}
                disabled={!!dniData?.document_id}
                maxlength={9}
              />
            </IonCol>
            <IonCol>
              <IonInput
                label="Fecha de nacimiento"
                labelPlacement="stacked"
                value={dayjs(dniData?.birth_date).format('DD/MM/YYYY')}
                disabled={!!dniData?.document_id}
              />
            </IonCol>
          </IonRow>
          <IonRow className={styles.modalFormRow}>
            <IonCol>
              <IonInput
                label="País de nacimiento"
                labelPlacement="stacked"
                className={classNames(styles.lowerInput, 'system-font')}
                value={
                  countryList.find(
                    (x: any) => x.code === (dniData?.birth_country || dniData?.nationality),
                  )?.description_code
                }
                disabled={!!dniData?.document_id}
              />
              {/* <IonSelect
                label="País de nacimiento"
                labelPlacement="stacked"
                className="lower-input"
                value={countryList.find((x: any) => x.code === dniData?.nationality)?.description_code}
                interface="popover"
                disabled={!!dniData?.document_id}
              >
                {countryList.map((status: KeyModel) => <IonSelectOption value={status.code} key={status.code} className="entry-product-option">{status.description_code}</IonSelectOption>)}
              </IonSelect> */}
            </IonCol>
          </IonRow>
        </IonGrid>
      </div>
      <IonButtons className={styles.nextBtnWrapper}>
        <IonButton
          className="nextButton"
          onClick={handleContinue}
          disabled={
            !emailValue || !phoneValue || !!emailError || !!phoneError
          }
        >
          Continuar
        </IonButton>
      </IonButtons>
    </div>
  );
}
